import React, { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Col, Drawer, Form, Input, notification, Row } from 'antd';
import PropTypes from 'prop-types';

import UserJobService from '@src/services/UserJobService';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FilterContainer from 'components/FilterContainer';
import { KeyValueWidget } from 'components/KeyValueWidget';

import { FORMS } from 'packages/jobs/constants';

import { FORM_LAYOUT_DEFAULT } from 'constants/commons';

import IntlMessages from 'util/IntlMessages';

export default function JobForm({ visible, onClose, id, jobs = [] }) {
  const [form] = Form.useForm();
  const intl = useIntl();
  const jobId = id;

  const orgId = useSelector(({ organizations }) => organizations.organization.id);
  const userJobService = useMemo(() => new UserJobService(), []);

  const [loading, setLoading] = React.useState(false);

  const { Item } = Form;

  const onSubmit = async values => {
    try {
      const existingJob = jobs.find(
        job => job.name.toLowerCase() === values.name.toLowerCase() && (!jobId || job.id !== jobId),
      );
      if (existingJob) {
        const message = intl.formatMessage({ id: 'job.form.name.exists' });
        notification.error({ message });
        return;
      }
      const action = () =>
        !jobId
          ? userJobService.create({ ...values, organizationId: orgId })
          : userJobService.update({ ...values, organizationId: orgId, id: jobId });

      setLoading(true);
      const result = await action();
      const message = intl.formatMessage({ id: 'general.save.successful.message' });
      notification.success({ message });
      const createdJob = !jobId ? result : { id: jobId, name: values.name };
      onClose(createdJob);
    } catch (error) {
      const message = intl.formatMessage({ id: 'general.save.error.message' });
      notification.error({ message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      const fetch = async () => {
        const res = await userJobService.getUserJob({ organizationId: orgId, userJobId: jobId });
        form.setFieldsValue({ name: res.name });
      };

      fetch();
    }
  }, [form, jobId, orgId, userJobService]);

  return (
    <Drawer closable={null} footer={null} open={visible}>
      <BoxContainer>
        <BoxContainer shadow fullContent fixed>
          <FilterContainer
            goBack={onClose}
            buttonItems={[
              {
                iconName: 'save',
                action: form.submit,
                disabled: loading,
              },
            ]}
            title={
              <Title.Header
                value={
                  <IntlMessages id={jobId ? 'position.form.edit.title' : 'position.form.title'} />
                }
              />
            }
          />
        </BoxContainer>
        <BoxContainer fullContent loading={loading}>
          <Form form={form} onFinish={onSubmit} layout={FORM_LAYOUT_DEFAULT} name={FORMS.position}>
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Title.SubTitle value={<IntlMessages id="position.form.job.information" />} />
              </Col>
              <Col span={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm required value={<IntlMessages id="position.form.name" />} />
                  }
                  value={
                    <Item
                      rules={[
                        {
                          required: true,
                          message: <IntlMessages id="form.required" />,
                        },
                      ]}
                      name="name"
                    >
                      <Input />
                    </Item>
                  }
                />
              </Col>
            </Row>
          </Form>
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
}

JobForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  id: PropTypes.string,
  jobs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
};

JobForm.defaultProps = {
  jobs: [],
};
